import React, { useEffect, useState } from "react";

import { apiErrorMessage, apiGet, listify } from "../../api";
import { displayInvoiceNumber } from "../../utils/invoiceNumber";
import { feesOf } from "./prefillFromInvoice";

// What this client was billed the last few times, in view while the fee is
// decided. Michael, 2026-10-26: "On the invoice input form, I should be able
// to see the amounts of the previous invoices somewhere."
//
// One request: the LIST rows already carry every figure shown here (the list
// serializer drops only `lines`), so nothing is fetched per invoice. Drafts
// are left out -- they were never billed.
//
// The fee column is feesOf(), the same reading the prefill uses, not
// `subtotal`: on the ~1,250 imported invoices `subtotal` holds the pre-tax
// figure INCLUDING disbursements, and showing it as the fee would count the
// disbursements twice.

const SHOWN = 5;

const STATUS_LABEL = { FINAL: "Issued", SENT: "Sent", PAID: "Paid", DRAFT: "Draft" };

const n = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

const money = (v) =>
  `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const TH = {
  padding: "4px 6px", fontSize: 11, fontWeight: 600, color: "#6B7280",
  textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "right",
  borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap",
};
const TD = {
  padding: "4px 6px", fontSize: 12, textAlign: "right", whiteSpace: "nowrap",
  borderBottom: "1px solid #F3F4F6", fontVariantNumeric: "tabular-nums",
};
const LEFT = { textAlign: "left" };

const newestFirst = (a, b) =>
  a.date === b.date ? n(b.id) - n(a.id) : (a.date < b.date ? 1 : -1);

export default function PreviousInvoicesPanel({ clientId, excludeInvoiceId }) {
  const [rows, setRows] = useState(null); // null while loading
  const [error, setError] = useState("");

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    setRows(null);
    setError("");
    apiGet(`/api/invoices/?client=${encodeURIComponent(clientId)}&exclude_status=DRAFT`)
      .then((data) => {
        if (!cancelled) setRows(listify(data));
      })
      .catch((e) => {
        if (cancelled) return;
        setRows([]);
        setError(apiErrorMessage(e, "Could not load previous invoices."));
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (!clientId) return null;

  const shown = (rows || [])
    .filter((r) => String(r.id) !== String(excludeInvoiceId ?? ""))
    .filter((r) => (r.status || "").toUpperCase() !== "DRAFT")
    .sort(newestFirst)
    .slice(0, SHOWN);

  return (
    <div
      style={{
        marginTop: 14, padding: "10px 12px", border: "1px solid #E5E7EB",
        borderRadius: 7, background: "#FAFAFA",
      }}
    >
      <div style={{ fontWeight: 600, color: "#374151", fontSize: 13, marginBottom: 6 }}>
        Previous invoices
      </div>
      {error ? (
        <div style={{ fontSize: 12, color: "#991B1B" }}>
          Could not load previous invoices. {error !== "Could not load previous invoices." ? error : ""}
        </div>
      ) : rows === null ? (
        <div style={{ fontSize: 12, color: "#6B7280" }}>Loading…</div>
      ) : !shown.length ? (
        <div style={{ fontSize: 12, color: "#6B7280" }}>No previous invoices for this client.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...TH, ...LEFT }}>Invoice</th>
                <th style={{ ...TH, ...LEFT }}>Date</th>
                <th style={TH}>Fees</th>
                <th style={TH}>Discount</th>
                <th style={TH}>Disb.</th>
                <th style={TH}>Tax</th>
                <th style={TH}>Total</th>
                <th style={{ ...TH, ...LEFT }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const fees = feesOf(r) ?? n(r.subtotal);
                const tax = n(r.gst) + n(r.pst) + n(r.hst);
                const status = (r.status || "").toUpperCase();
                return (
                  <tr key={r.id}>
                    <td style={{ ...TD, ...LEFT }}>
                      <a
                        href={`/invoices/${r.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open this invoice in a new tab"
                        style={{ color: "#2563EB" }}
                      >
                        {displayInvoiceNumber(r.number)}
                      </a>
                    </td>
                    <td style={{ ...TD, ...LEFT }}>{r.date}</td>
                    <td style={TD}>{money(fees)}</td>
                    <td style={TD}>{money(n(r.discount))}</td>
                    <td style={TD}>{money(n(r.disbursements))}</td>
                    <td style={TD}>{money(tax)}</td>
                    <td style={{ ...TD, fontWeight: 600 }}>{money(n(r.total))}</td>
                    <td style={{ ...TD, ...LEFT }}>{STATUS_LABEL[status] || status}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
